import React, { useMemo, useCallback } from "react";
import { Icon } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";

// Common
import DateTextBox from "react-lib/apps/common/DateTextBox";

// UX
import DPOHistoryReviewExUX from "./DPOHistoryReviewExUX";
import ModalItemFilterCustom from "./ModalItemFilterCustom";
import ModalItemFilterLevelCustom from "./ModalItemFilterLevelCustom";

// Utils
import * as utils from "react-lib/utils";
import { formatUTCtoBEString } from "react-lib/utils/dateUtils";
import { tidyLocation2 } from "react-lib/apps/common/CUDENT/DentalRecordUtil";

// Types
export type ReviewExProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: Function) => any;

  // data
  columnsHistoryEx?: any[];
  historyReviewExData: any;
  patientId: any;
  tableHeight?: string;
  languageUX: string;
  historyDate: any;
  organItems?: any[];
  showDrawingChecked?: any[];
  // filter
  doctorFilterListEx?: any[];
  clinicFilterListEx?: any[];
  locationFilterList?: any[];
  clinicalFindingFilterList?: any[];
  openModalDoctorExFilter?: boolean;
  openModalFilterClinicEx?: boolean;
  openModalFilterLocation?: boolean;
  openModalFilterClinicalFinding?: boolean;

  // callback
  handleGetHistoryEx: () => any;
  handleOnFocusFindingsRowEx: (state: any, rowInfo: any) => any;
  onChangeDate?: (key: "start" | "end") => (date: string) => any;
};

const ReviewEx = (props: ReviewExProps) => {
  const intl = useIntl();

  // Callback
  const checkHistoryFilterActive = useCallback((list: any[] = []) => {
    return list.find((item: any) => item.is_selected === false);
  }, []);

  const renderDateCell = useCallback((row: any) => {
    let [dateComponent, timeComponent] = ["", ""];
    if (row.original?.created) {
      [dateComponent, timeComponent] = formatUTCtoBEString(row.original.created) as any[];
    }
    return <div style={{ textAlign: "center" }}>{`${dateComponent} ${timeComponent}`}</div>;
  }, []);

  const renderLocationCell = useCallback(
    (row: any) => {
      let locationList = row.original.locations_name?.split(",").map((item: any) => {
        let superTooth = props.organItems?.find((o: any) => o.fullName === item);
        if (superTooth) {
          return superTooth.nickName;
        }
        return item.trim();
      });

      let Location;

      Location = tidyLocation2(locationList, props.organItems || []);
      return <div style={{ textAlign: "center" }}>{`${Location} `}</div>;
    },
    [props.organItems]
  );

  const renderDiagramCell = useCallback(
    (row: any) => {
      return (
        <div style={{ textAlign: "center" }}>
          {row.original.image ? (
            <>
              <Icon
                name={props.showDrawingChecked?.includes(row.original.id) ? "eye" : "eye slash"}
                color={props.showDrawingChecked?.includes(row.original.id) ? "yellow" : "grey"}
                onClick={(e: any) => {
                  props.onEvent({ message: "SetToggleShowDrawingOverlay", params: { row: row.original } });
                }}
                style={{ cursor: "pointer" }}
              />{" "}
            </>
          ) : (
            <> {intl.formatMessage({ id: "cukey234" })} </>
          )}
        </div>
      );
    },
    [props.showDrawingChecked]
  );

  // Memo
  const columnsHistoryEx = useMemo(() => {
    const columns = [
      {
        Header: "Date",
        resizeable: false,
        accessor: "created",
        Cell: renderDateCell,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {intl.formatMessage({ id: "cukey65" })}
            <Icon
              className="review-ex-filter-doctor"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.doctorFilterListEx) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalDoctorExFilter", !props.openModalDoctorExFilter)}
            />
          </div>
        ),
        accessor: "edit_user_name",  // "doctor_name",
        resizeable: false,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {intl.formatMessage({ id: "cukey54" })}
            <Icon
              className="review-ex-filter-clinic"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.clinicFilterListEx) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalFilterClinicEx", !props.openModalFilterClinicEx)}
            />
          </div>
        ),
        accessor: "division_name",
        resizeable: false,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {intl.formatMessage({ id: "cukey112" })}
            <Icon
              className="review-ex-filter-location"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.locationFilterList) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalFilterLocation", !props.openModalFilterLocation)}
            />
          </div>
        ),
        accessor: "locations_name",
        resizeable: false,
        keySuffix: "locations_name",
        masterDataSet: "organ",
        Cell: renderLocationCell,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {intl.formatMessage({ id: "cukey56" })}
            <Icon
              className="review-ex-filter-clinical"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.clinicalFindingFilterList) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalFilterClinicalFinding", !props.openModalFilterClinicalFinding)}
            />
          </div>
        ),
        accessor: "clinical_findings_name",
        resizeable: false,
      },
      {
        Header: "Principal diagnosis",
        accessor: "icd10_label",
        resizeable: false,
      },
      {
        Header: "Treatment plan",
        accessor: "dental_treatment_plan_label",
        resizeable: false,
      },
      {
        Header: "Diagram",
        accessor: "diagram",
        resizeable: false,
        Cell: renderDiagramCell,
      },
      ...(props.columnsHistoryEx || []),
    ];

    return Array.from(new Map(columns.map((item) => [item.Header, item])).values());
  }, [
    props.columnsHistoryEx,
    props.doctorFilterListEx,
    props.clinicFilterListEx,
    props.locationFilterList,
    props.clinicalFindingFilterList,
    props.openModalDoctorExFilter,
    props.openModalFilterClinicEx,
    props.openModalFilterLocation,
    props.openModalFilterClinicalFinding,
    renderLocationCell,
    renderDiagramCell,
  ]);

  // Handler
  const handleLocationFilter = (list: any[]) => {
    props.onEvent({
      message: "SetLocationFilter",
      params: {
        filter: list,
      },
    });
  };

  return (
    <DPOHistoryReviewExUX
      columnsHistoryEx={columnsHistoryEx}
      cardFilterDentistExElement={
        props.openModalDoctorExFilter ? (
          <ModalItemFilterCustom
            name="Dentist"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.doctorFilterListEx}
            onClose={() => props.setProp("openModalDoctorExFilter", false)}
            textField="name"
            target="review-ex-filter-doctor"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={(list) =>
              props.onEvent({
                message: "SetDoctorFilterEx",
                params: {
                  filter: list,
                },
              })
            }
          />
        ) : null
      }
      cardFilterLocationElement={
        props.openModalFilterLocation ? (
          <ModalItemFilterLevelCustom
            title="Location"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.locationFilterList || []}
            onClose={() => props.setProp("openModalFilterLocation", false)}
            level1="tissue__name"
            level2="name"
            target="review-ex-filter-location"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={handleLocationFilter}
          />
        ) : null
      }
      cardFilterClinicExElement={
        props.openModalFilterClinicEx ? (
          <ModalItemFilterCustom
            name="Clinic"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.clinicFilterListEx}
            onClose={() => props.setProp("openModalFilterClinicEx", false)}
            textField="name"
            target="review-ex-filter-clinic"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={(list) =>
              props.onEvent({
                message: "SetClinicFilterEx",
                params: {
                  filter: list,
                },
              })
            }
          />
        ) : null
      }
      cardFilterClinicalFindingElement={
        props.openModalFilterClinicalFinding ? (
          <ModalItemFilterLevelCustom
            title="Clinical finding"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.clinicalFindingFilterList || []}
            onClose={() => props.setProp("openModalFilterClinicalFinding", false)}
            level1="group"
            level2="name"
            target="review-ex-filter-clinical"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={(list) =>
              props.onEvent({
                message: "SetClinicalFindingFilter",
                params: {
                  filter: list,
                },
              })
            }
          />
        ) : null
      }
      historyReviewExData={props.historyReviewExData}
      handleGetHistoryEx={props.handleGetHistoryEx}
      handleOnFocusFindingsRowEx={props.handleOnFocusFindingsRowEx}
      setProp={props.setProp}
      onEvent={props.onEvent}
      patientId={props.patientId}
      tableHeight={props.tableHeight}
      languageUX={props.languageUX}
      exStartDate={
        <DateTextBox
          name="exStartDate"
          maxDate={utils.adToBe(moment())}
          value={props.historyDate.exStartDate}
          // onChange={(date) => {
          //   props.setProp("historyDate.exStartDate", date);
          // }}
          onChange={props.onChangeDate?.("start")}
        />
      }
      exEndDate={
        <DateTextBox
          name="exEndDate"
          maxDate={utils.adToBe(moment())}
          value={props.historyDate.exEndDate}
          // onChange={(date) => {
          //   props.setProp("historyDate.exEndDate", date);
          // }}
          onChange={props.onChangeDate?.("end")}
        />
      }
    />
  );
};

ReviewEx.displayName = "ReviewEx";

export default React.memo(ReviewEx);
